function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

// Keep programmatic focus restoration accessible without drawing a ring after a tap.
// Safari can carry :focus-visible from a dialog back to its pointer-activated opener.
(() => {
  const setPointer = () => { document.documentElement.dataset.saInput = 'pointer'; };
  document.addEventListener('pointerdown', setPointer, true);
  document.addEventListener('touchstart', setPointer, {capture: true, passive: true});
  document.addEventListener('mousedown', setPointer, true);
  document.addEventListener('keydown', (event) => {
    if (['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) return;
    document.documentElement.dataset.saInput = 'keyboard';
  }, true);
})();

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }

  // Closes the drawer at once, without the closing animation. Returns whether it was open.
  closeImmediately() {
    const main = this.mainDetailsToggle;
    if (!main || (!main.hasAttribute('open') && !main.classList.contains('menu-opening'))) return false;
    main.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    main.querySelectorAll('.submenu-open').forEach((submenu) => submenu.classList.remove('submenu-open'));
    main.classList.remove('menu-opening');
    main.removeAttribute('open');
    main.querySelectorAll('summary').forEach((summary) => summary.setAttribute('aria-expanded', 'false'));
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus();
    if (main.contains(document.activeElement)) document.activeElement.blur();
    return true;
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };

  closeImmediately() {
    if (!super.closeImmediately()) return false;
    (this.header || document.querySelector('.section-header'))?.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
    return true;
  }
}

customElements.define('header-drawer', HeaderDrawer);

// The back/forward cache returns a page exactly as it was left. A shopper who left through a link in the menu
// drawer or search came back to the drawer still open over the page, with page scrolling locked. Close the
// header's drawers, search and dropdowns at once. (A Back that reloads the page starts with them closed.)
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  document.querySelectorAll('header-drawer, menu-drawer').forEach((drawer) => drawer.closeImmediately?.());
  document.querySelectorAll('details-modal').forEach((modal) => {
    if (modal.isOpen?.()) modal.close(false);
  });
  document.querySelectorAll('header-menu').forEach((menu) => {
    if (menu.mainDetailsToggle?.hasAttribute('open')) menu.close();
  });
});

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      this.dispatchProductSelectEvent();

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  getAllSelectedOptions() {
    const options = [];
    this.querySelectorAll('fieldset, .product-form__input--dropdown').forEach((group) => {
      const checked = group.querySelector('input:checked') || group.querySelector('select option[selected]');
      if (checked) {
        options.push({ name: checked.dataset.optionName || '', value: checked.value });
      }
    });
    return options;
  }

  dispatchProductSelectEvent() {
    const { ProductSelectEvent } = window.StandardEvents || {};
    if (!ProductSelectEvent) return;

    const deferred = ProductSelectEvent.createPromise();
    this.pendingSelectPromise = deferred;

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId,
          title: this.dataset.productTitle,
          handle: this.dataset.productHandle,
        },
        selectedOptions: this.getAllSelectedOptions(),
        promise: deferred.promise,
      })
    );
  }

  takePendingSelectPromise() {
    const deferred = this.pendingSelectPromise;
    this.pendingSelectPromise = null;
    return deferred;
  }

  resolvePendingSelectPromise(variant, sourceVariantSelects = this) {
    const deferred = this.takePendingSelectPromise();
    if (!deferred) return;

    if (variant) {
      deferred.resolve({
        variant: {
          id: variant.id,
          title: variant.title,
          availableForSale: variant.available,
          price: {
            amount: sourceVariantSelects?.dataset.selectedPriceAmount,
            currencyCode: sourceVariantSelects?.dataset.currencyCode,
          },
          selectedOptions: this.getAllSelectedOptions(),
        },
      });
    } else {
      deferred.resolve({ variant: null });
    }
  }

  rejectPendingSelectPromise(error) {
    this.takePendingSelectPromise()?.reject(error);
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      // Values stay English in Shopify; French, Spanish and German pages show them through window.SA_I18N.
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = window.SA_I18N?.[`option_value.${value}`] ?? value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  getCartQuantityForLine(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    return parseInt(input?.dataset.cartQuantity || input?.getAttribute('value') || '0', 10) || 0;
  }

  startCartLinesUpdate(items) {
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!CartLinesUpdateEvent) return;

    const linesByAction = Object.entries(items).reduce((groups, [variantId, quantity]) => {
      const nextQuantity = parseInt(quantity, 10);
      const currentQuantity = this.getCartQuantityForLine(variantId);

      if (Number.isNaN(nextQuantity) || currentQuantity === nextQuantity) return groups;

      const action = currentQuantity === 0 ? 'add' : nextQuantity === 0 ? 'remove' : 'update';
      let line;
      if (action === 'add') {
        line = { merchandiseId: variantId, quantity: nextQuantity };
      } else {
        const lineKey = this.querySelector(`[data-quantity-variant-id="${variantId}"]`)?.dataset.quantityLineKey;
        // No AJAX line key on the row — likely cached HTML rendered before this
        // attribute landed. Skip rather than emit an event with id: ''.
        if (!lineKey) return groups;
        line = { id: lineKey, quantity: nextQuantity };
      }

      if (!groups[action]) groups[action] = [];
      groups[action].push(line);
      return groups;
    }, {});

    const deferreds = Object.entries(linesByAction).map(([action, lines]) => {
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action,
          context: 'product',
          lines,
          promise: deferred.promise,
        })
      );
      return deferred;
    });

    return {
      resolve: (parsedState) => {
        const payload = { cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedState) };
        deferreds.forEach((deferred) => deferred.resolve(payload));
      },
      reject: (error) => {
        deferreds.forEach((deferred) => deferred.reject(error));
      },
    };
  }

  dispatchCartErrorEvent(message, code) {
    const { CartErrorEvent } = window.StandardEvents || {};
    if (!CartErrorEvent) return;

    this.dispatchEvent(new CartErrorEvent({ error: message, code }));
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}


// One accepted selection and one cart operation for PDP, sticky bar and scanner.
const spaceArmoryOpenOverlay = 'dialog[open], modal-dialog[open], quick-add-modal[open], product-modal[open]';
const spaceArmorySizeAliases = {S:['s','small'],M:['m','medium'],L:['l','large'],XL:['xl','x-large','extra large'],XXL:['xxl','2xl','xx-large','extra extra large']};
class SpaceArmoryPurchaseController {
  // Text in the shopper's language: snippets/sa-i18n-js.liquid sets window.SA_I18N on French, Spanish and German
  // pages only. English pages have no dictionary, so every string falls back to its English text unchanged.
  // French, Spanish and German pages write decimals with a comma (14,5 cm); English pages keep the point.
  static decimal(value) {
    const text = String(value ?? '');
    return window.SA_I18N ? text.replace(/(\d)\.(\d)/g, '$1,$2') : text;
  }
  static text(key, fallback, vars) {
    let text = window.SA_I18N?.[key] ?? fallback;
    if (vars) for (const [name, value] of Object.entries(vars)) text = text.replaceAll(`{${name}}`, value);
    return text;
  }
  constructor(info, restored) {
    this.info = info;
    this.root = info.querySelector('face-fit-scanner');
    this.names = JSON.parse(this.root.dataset.productOptions || '[]');
    this.stickyPrices = JSON.parse(info.querySelector('[data-sticky-prices]')?.textContent || 'null');
    this.variants = JSON.parse(this.root.querySelector('[data-face-fit-variants]').textContent);
    this.bundleSizes = this.root.dataset.bundleSizes === 'true';
    // PDP v2: Add to Cart stays visible; size is still required before adding.
    this.v2 = this.root.dataset.pdpV2 === 'true';
    // Size is recognised by its English name and by the French/Spanish/German/Italian names, in case the option name
    // is ever translated in Shopify again (23 Oct 2026: 417 translated names broke the size lock on /fr /es /de).
    this.sizeIndices = this.names.flatMap((name, index) => (/^(size|fit|beden|size fit|taille|talla|größe|groesse|taglia)$/i.test(name.trim()) || (this.bundleSizes && /( (size|fit|beden)$|^(taille|talla|größe|groesse|taglia) \S)/i.test(name.replace(/[()]/g, ' ').trim()))) ? [index] : []);
    this.sizeIndex = restored?.sizeIndex ?? this.sizeIndices[0];
    this.abort = new AbortController();
    this.error = '';
    this.pending = restored?.pending || null;
    this.hasCompletedScan = restored?.hasCompletedScan === true;
    this.savedMeasurement = restored?.savedMeasurement || null;
    this.profileKey = restored?.profileKey || '';
    this.hasFreshScan = restored?.hasFreshScan || false;
    this.freshMeasurement = restored?.freshMeasurement || null;
    // Measure by Hand → Use Size on this page. Shown here only; it never follows to another mask (useManual).
    this.manualMeasurement = restored?.manualMeasurement || null;
    // The measurement whose card gave way to the two Measure rows (Measure Again). A new measurement brings the card back.
    this.againFor = restored?.againFor || null;
    this.profileCustomer = restored?.profileCustomer || null;
    // The measurement remembered from any mask page (SpaceArmoryFitMemory), and whether it chose this page's size.
    this.rememberedMeasurement = restored?.rememberedMeasurement || null;
    this.rememberedApplied = restored?.rememberedApplied === true;
    this.revision = 0;
    this.values = this.readValues();
    const urlVariant = this.variants.find(v => String(v.id) === new URL(location.href).searchParams.get('variant'));
    if (restored) this.values = [...restored.values];
    else {
      // The URL's variant sets the other options (e.g. fang colour, strap) as they come. Its size, like a size the
      // browser restored into the controls, is not the shopper's choice by itself (admitSize).
      if (urlVariant) this.values = [...urlVariant.options];
      this.sizeIndices.forEach(index => { this.values[index] = this.admitSize(index, urlVariant?.options[index] || ''); });
      // A hand measurement made on this page comes back with the size a reload or Back restores (readManual).
      this.manualMeasurement = this.readManual();
    }
    if (this.bundleSizes) {
      const target = this.root.querySelector('[data-bundle-size-target]');
      if (target) target.value = String(this.sizeIndex);
    }
    info.faceFitPurchase = this;
    info.classList.add('face-fit-purchase-ready');
    this.writeControls();
    this.sync();
    if (this.pending) {
      const inFlight = this.pending;
      const settle = () => { if (this.pending === inFlight) { this.pending = null; this.removeRememberedProperties(); this.flushRemembered(); this.sync(); } };
      inFlight.then(settle, error => { this.error = error.message; settle(); });
    }
    const signal = this.abort.signal;
    info.addEventListener('change', event => {
      if (event.target.matches('[data-bundle-size-target]')) {
        if (this.pending) return;
        this.sizeIndex = Number(event.target.value);
        this.savedMeasurement = null;
        this.hasFreshScan = false;
        this.clearProfileProperties();
        this.root.dispatchEvent(new CustomEvent('space-armory:clear-profile'));
        this.sync();
        return;
      }
      this.onChange(event);
    }, {capture: true, signal});
    info.addEventListener('click', event => {
      const same = event.target.closest('[data-sa-same-size]');
      if (same && this.setAll) { event.preventDefault(); this.applySameSize(same); return; }
      const target = event.target.closest('[data-purchase-primary], [data-purchase-scan], [data-purchase-manual], [data-purchase-again], [data-purchase-change]');
      if (!target) return;
      event.preventDefault();
      if (target.matches('[data-purchase-primary]')) this.primaryAction(event);
      else if (target.matches('[data-purchase-scan]')) this.openScanner();
      else if (target.matches('[data-purchase-again]')) this.measureAgain();
      else if (target.matches('[data-purchase-manual]')) this.openManual(target);
      else this.focusSizes();
    }, {signal});
    const profile = window.SpaceArmoryFitAccount;
    profile?.addEventListener('change', () => this.acceptProfile(profile.state), {signal});
    if (profile) this.acceptProfile(profile.state);
    // Measured on another mask page: preselect the same size here unless a size or the account's measurement wins.
    this.useRemembered(window.SpaceArmoryFitMemory?.read());
    // A measurement applied in another tab follows here only while this page has no size picked by hand.
    window.addEventListener('storage', event => {
      if (event.key === null || event.key === window.SpaceArmoryFitMemory?.key) this.refreshRemembered(true);
    }, {signal});
    info.addEventListener('space-armory:scanner-visibility', () => this.updateSticky(), {signal});
    info.addEventListener('space-armory:scan-complete', event => {
      if (event.target !== this.root || !window.SpaceArmoryFitRules?.valid(event.detail?.measurement)) return;
      this.hasFreshScan = true;
      this.freshMeasurement = event.detail?.measurement;
      if (this.bundleSizes && !this.setAll && this.form) {
        let target = this.form.querySelector('input[name="properties[Measurement for]"]');
        if (!target) { target = document.createElement('input'); target.type = 'hidden'; target.name = 'properties[Measurement for]'; this.form.append(target); }
        target.value = this.names[this.sizeIndex];
      }
      this.savedMeasurement = null;
      this.form?.querySelector('input[name="properties[_Face Fit Action]"]')?.remove();
      this.sync();
    }, {signal});
    window.addEventListener('scroll', () => this.updateSticky(), {passive: true, signal});
    window.addEventListener('resize', () => this.updateSticky(), {passive: true, signal});
    window.addEventListener('pageshow', event => {
      // From the back/forward cache the page is exactly as the shopper left it. On a fresh load the browser may
      // restore the radios it saved when the page was left after this controller ran (Chrome does so after
      // DOMContentLoaded): restored non-size options are kept, a restored size only if it is the one the shopper
      // last chose for this product in this tab.
      const current = this.values;
      this.values = this.readValues().map((value, index) => !event.persisted && this.sizeIndices.includes(index) &&
        value !== current[index] && !this.choseSize(index, value) ? current[index] || '' : value);
      this.writeControls();
      this.sync();
      // Back/forward cache: a measurement applied on the page the visitor came back from is not announced by 'storage'.
      if (event.persisted) this.refreshRemembered();
    }, {signal});
    this.intersection = typeof IntersectionObserver === 'function' ? new IntersectionObserver(() => this.updateSticky()) : null;
    this.intersection?.observe(this.stickyBoundary());
    this.updateSticky();
  }
  acceptProfile(state) {
    this.noteAccount(state);
    if (this.bundleSizes && this.sizeIndices.length > 1 && !this.setAll) return;
    if (state.status === 'loading' || this.pending) return;
    const identityChanged = this.profileCustomer && this.profileCustomer !== state.customer;
    const key = `${state.customer || ''}:${state.revision}:${state.status}`;
    if (key === this.profileKey) return;
    this.profileKey = key;
    this.profileCustomer = state.customer;
    if (identityChanged) { this.hasFreshScan = false; this.freshMeasurement = null; }
    if (!state.measurement || identityChanged) {
      if (this.savedMeasurement || identityChanged) {
        this.savedMeasurement = null; this.hasCompletedScan = false;
        this.clearSizes(); this.writeControls();
        this.clearProfileProperties();
        // Signing out, or removing the saved measurement, also forgets the copy this browser remembers.
        // A failed account check (status 'error') is not a removal.
        if (identityChanged || state.status === 'ready') this.forgetRemembered();
        this.root.dispatchEvent(new CustomEvent('space-armory:clear-profile'));
      }
      this.sync();
      if (!state.measurement) {
        // A remembered record kept next to a saved account measurement waits for this check (useRemembered).
        const remembered = window.SpaceArmoryFitMemory?.read();
        if (remembered) this.useRemembered(remembered);
        return;
      }
    }
    if (!window.SpaceArmoryFitRules?.valid(state.measurement)) return;
    if (this.hasFreshScan) {
      if(state.measurement.measuredAt!==this.freshMeasurement?.measuredAt || state.measurement.cm!==this.freshMeasurement?.cm)return;
      this.hasFreshScan=false;
    }
    this.savedMeasurement = {...state.measurement, customer:state.customer, revision:state.revision};
    this.hasCompletedScan = true;
    // The account's measurement wins over the remembered one.
    this.rememberedApplied = false; this.removeRememberedProperties();
    const size = window.SpaceArmoryFitRules.sizeFor(state.measurement.cm);
    const aliases = {S:['s','small'],M:['m','medium'],L:['l','large'],XL:['xl','x-large','extra large'],XXL:['xxl','2xl','xx-large','extra extra large']};
    const group = this.groups()[this.sizeIndex];
    const control = Array.from(group?.querySelectorAll('input[type="radio"], select option') || []).find(el => aliases[size]?.includes(el.value.trim().toLowerCase()));
    this.values[this.sizeIndex] = control?.value || '';
    // Set pages v2: the account's measurement sizes every mask of the set.
    if (this.setAll && control) this.fillSizes(size);
    this.error = control ? '' : SpaceArmoryPurchaseController.text('pdp.saved_size_not_offered', 'Your saved size is not offered for this product.');
    this.writeControls(); this.sync();
    if(control && this.resolve()?.available){
      this.applyingProfile=true;
      try{(control.tagName==='OPTION'?control.parentElement:control).dispatchEvent(new Event('change',{bubbles:true}));}
      finally{this.applyingProfile=false;}
    }
  }
  // --- Remembered measurement (assets/face-fit-memory.js) -------------------------------------------------------
  // The latest measurement the visitor applied on any mask page. Like a saved account measurement it preselects the
  // size on mask pages (PDP v2, never bundles) while no size is chosen; the account's measurement, a size picked by hand
  // and a size a store link brought back all win. On a reload, Back or a restored tab the size the visitor last chose
  // for this mask in this tab wins too; a size the browser or an ad link brought back that the visitor did not choose
  // never does (admitSize): the page starts without one and this preselects. The record stays when another size is picked.
  sizeControl(size) {
    return Array.from(this.groups()[this.sizeIndex]?.querySelectorAll('input[type="radio"], select option') || [])
      .find(el => (spaceArmorySizeAliases[size] || []).includes(el.value.trim().toLowerCase())) || null;
  }
  useRemembered(record, {follow = false} = {}) {
    if (!this.v2 || (this.bundleSizes && !this.setAll)) return;
    const size = record && window.SpaceArmoryFitRules?.sizeFor(record.cm);
    if (!size) return;
    // A record kept next to a saved account measurement waits while the account loads: that measurement wins, or its
    // removal clears the record (noteAccount). A failed check ('error') is no removal, so the visitor's own size applies.
    if (record.account === 'saved' && window.SpaceArmoryFitAccount?.state?.status === 'loading') return;
    this.rememberedMeasurement = record;
    // follow: a newer measurement replaces the size the previous remembered one chose. On a set page v2 it sizes every
    // mask, and only while no mask has a size yet.
    const free = (this.setAll ? this.sizeIndices.every(index => !this.values[index]) : !this.size) || (follow && this.rememberedApplied);
    const control = free && !this.savedMeasurement && !this.pending ? this.sizeControl(size) : null;
    if (!control) { this.sync(); return; }
    this.rememberedApplied = true;
    if (this.setAll ? this.sizeIndices.every(index => this.values[index] === this.values[this.sizeIndex]) && control.value === this.size : control.value === this.size) { this.sync(); return; }
    this.values[this.sizeIndex] = control.value;
    if (this.setAll) this.fillSizes(size);
    this.error = '';
    this.writeControls(); this.sync();
    if (this.resolve()?.available) {
      this.applyingProfile = true;
      try { (control.tagName === 'OPTION' ? control.parentElement : control).dispatchEvent(new Event('change', {bubbles: true})); }
      finally { this.applyingProfile = false; }
    }
  }
  // storage event (another tab) or a page restored from the back/forward cache.
  refreshRemembered(fromStorageEvent = false) {
    // An add to cart in flight keeps its size and card; follow once it settles (flushRemembered).
    if (this.pending) { this.rememberedQueued = {fromStorageEvent: fromStorageEvent || !!this.rememberedQueued?.fromStorageEvent}; return; }
    const record = window.SpaceArmoryFitMemory?.read() || null;
    const current = this.rememberedMeasurement;
    if (record && current && record.measuredAt === current.measuredAt && record.cm === current.cm && record.method === current.method) return;
    if (record) { this.useRemembered(record, {follow: true}); return; }
    // Cleared in another tab (sign-out, site data). Without a storage event an empty read may only mean blocked storage.
    if (fromStorageEvent && current) {
      this.rememberedMeasurement = null; this.rememberedApplied = false; this.removeRememberedProperties();
      this.sync();
    }
  }
  flushRemembered() {
    const queued = this.rememberedQueued;
    if (!queued || this.pending) return;
    this.rememberedQueued = null;
    this.refreshRemembered(queued.fromStorageEvent);
  }
  forgetRemembered() {
    window.SpaceArmoryFitMemory?.clear();
    this.rememberedMeasurement = null; this.rememberedApplied = false; this.removeRememberedProperties();
  }
  noteAccount(state) {
    const memory = window.SpaceArmoryFitMemory;
    if (!memory || state?.status !== 'ready') return;
    memory.noteAccount(state);
    // Judged on this page's own copy: face-fit-memory.js may have handled the same check first, and blocked storage
    // keeps nothing to compare with.
    if (!this.rememberedMeasurement) return;
    const next = memory.afterAccount(this.rememberedMeasurement, state);
    if (next) { this.rememberedMeasurement = next; return; }
    // Signed out, or the saved measurement was removed: like a saved account size, a size the record chose goes too.
    if (this.rememberedApplied && !this.pending) { this.clearSizes(); this.writeControls(); }
    this.rememberedMeasurement = null; this.rememberedApplied = false; this.removeRememberedProperties();
    this.sync();
  }
  // Measure by Hand → Use Size (sa-pdp-v2.js): the size card shows the hand measurement on this page only. It is never
  // remembered for other masks, and a camera measurement kept from another page stays as it is.
  // shown: the calculator's result, {unit: 'in' | 'cm', value: '5.5', size: 'L'}. The card prints the width the way the
  // calculator showed it, in the unit it was typed in ("5.5 in", "5 5/8 in", "5½ in", "14.0 cm"), and the size it gave
  // (inches are sized on the chart's inch row, sa-pdp-v2.js inchSize). Without it the card prints the cm in the
  // visitor's unit.
  useManual(cm, shown = {}) {
    // A bundle with several mask sizes may be measuring someone else (set pages v2 size every mask, applySize).
    if (this.bundleSizes && this.sizeIndices.length > 1 && !this.setAll) return null;
    if (typeof cm !== 'number' || !window.SpaceArmoryFitRules?.sizeFor(cm)) return null;
    this.manualMeasurement = {cm, method: 'manual', measuredAt: new Date().toISOString()};
    // A decimal, a fraction or a whole number with a fraction sign, as sa-pdp-v2.js readWidth prints it.
    if ((shown?.unit === 'in' || shown?.unit === 'cm') && /^(\d+(\.\d+)?|(\d+ )?\d+\/\d+|\d*[½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞])$/.test(shown.value || '')) Object.assign(this.manualMeasurement, {unit: shown.unit, value: shown.value});
    if (['S', 'M', 'L', 'XL', 'XXL'].includes(shown?.size)) this.manualMeasurement.size = shown.size;
    this.keepManual(this.manualMeasurement);
    // The size is now the visitor's own: a remembered measurement no longer labels it or follows over it.
    this.rememberedApplied = false; this.removeRememberedProperties();
    this.sync();
    return this.manualMeasurement;
  }
  // A hand measurement survives a reload or Back of this page in this tab (owner, 11 Oct 2026), and only that: it is kept
  // in sessionStorage per product, never in the remembered record, so it never reaches another mask. It comes back only
  // with the size this load restores (admitSize: the size the shopper last chose here, never one from an ad link); any
  // other visit drops it. A camera result applied here or a new hand measurement replaces it.
  get manualKey() { return `sa-hand-measurement:${this.root.dataset.productId || location.pathname}`; }
  readManual() {
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(this.manualKey)); } catch (error) {}
    const navigation = SpaceArmoryPurchaseController.navigationType();
    const restores = (navigation === 'reload' || navigation === 'back_forward') && !!this.size && !(this.bundleSizes && this.sizeIndices.length > 1);
    const manual = restores && saved?.method === 'manual' && typeof saved.cm === 'number' && window.SpaceArmoryFitRules?.sizeFor(saved.cm) ? saved : null;
    if (!manual) this.keepManual(null);
    return manual;
  }
  keepManual(measurement) {
    try {
      if (measurement) sessionStorage.setItem(this.manualKey, JSON.stringify(measurement));
      else sessionStorage.removeItem(this.manualKey);
    } catch (error) {}
  }
  // Only an explicit camera apply keeps a measurement for every mask: the camera result buttons (fitment-scanner).
  // A shown result, a restored account record, a rejected result or a hand measurement never writes.
  remember(measurement) {
    // A bundle with several mask sizes may be measuring someone else (set pages v2 apply it to every mask, like a mask page).
    if (this.bundleSizes && this.sizeIndices.length > 1 && !this.setAll) return null;
    const record = window.SpaceArmoryFitMemory?.write(measurement, this.root.dataset.productHandle || this.root.dataset.productId);
    if (!record) return null;
    this.rememberedMeasurement = record;
    // This page's own apply already carries its attribution (or none, for Measure by Hand).
    this.rememberedApplied = false; this.removeRememberedProperties();
    this.sync();
    return record;
  }
  // A size preselected from the remembered measurement and kept unchanged is labelled on the cart line.
  attachRememberedProperties() {
    this.removeRememberedProperties();
    const rules = window.SpaceArmoryFitRules, record = this.rememberedMeasurement, form = this.form;
    if (!form || !rules || !record || !this.rememberedApplied || this.savedMeasurement) return;
    if (!(spaceArmorySizeAliases[rules.sizeFor(record.cm)] || []).includes(this.size.trim().toLowerCase())) return;
    // A measurement taken on this page keeps its own, product-specific properties.
    if (['Measured Size', '_Face Fit Measurement', '_Face Fit Source', '_Face Fit Action'].some(name => form.querySelector(`input[name="properties[${name}]"]`))) return;
    const properties = {
      ...this.measuredSizeProperties(record),
      '_Face Fit Source': `remembered | ${record.product} | ${record.rules}`,
      '_Face Fit Action': 'Use remembered camera measurement',
    };
    Object.entries(properties).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden'; input.name = `properties[${name}]`; input.value = value; input.dataset.faceFitRemembered = '';
      form.append(input);
    });
  }
  removeRememberedProperties() {
    this.form?.querySelectorAll('input[data-face-fit-remembered]').forEach(input => input.remove());
  }
  // The order line "Measured Size" as the shopper reads it in the cart, checkout, the order and its emails (owner,
  // 11 Oct 2026): the visitor's unit only, "5.67 in - L" on the United States country, "14.4 cm - L" elsewhere. The
  // hidden _Face Fit Width keeps the full width in both units, "14.4 cm / 5.67 in - L" (face-fit-rules.js text()).
  measuredSizeProperties(record) {
    const rules = window.SpaceArmoryFitRules, shown = rules.display(record.cm);
    return {'Measured Size': `${this.sizeUnit === 'in' ? `${shown.inches} in` : `${shown.cm} cm`} - ${shown.size}`, '_Face Fit Width': rules.text(record)};
  }
  clearProfileProperties() {
    this.form?.querySelectorAll('input[name^="properties["]').forEach(input => {
      if (['properties[Measurement for]', 'properties[Measured Size]', 'properties[_Face Fit Width]', 'properties[_Face Fit Profile Revision]', 'properties[_Face Fit Profile Customer]', 'properties[_Face Fit Source]', 'properties[_Face Fit Measurement]', 'properties[_Face Fit Action]', 'properties[_Face Fit Device]', ...(window.SpaceArmoryFitRules?.scanStatsKeys || []).map(key => `properties[${key}]`)].includes(input.name)) input.remove();
    });
  }
  attachProfileProperties() {
    if (!this.savedMeasurement) return;
    // A save completing after a fresh scan must not erase its purchase action.
    const action = this.form?.querySelector('input[name="properties[_Face Fit Action]"]')?.value;
    const sameResult = this.freshMeasurement?.cm === this.savedMeasurement.cm &&
      this.freshMeasurement?.measuredAt === this.savedMeasurement.measuredAt;
    this.clearProfileProperties();
    const properties = {
      ...this.measuredSizeProperties(this.savedMeasurement),
      '_Face Fit Profile Revision': String(this.savedMeasurement.revision),
      '_Face Fit Source': `saved_profile | ${window.SpaceArmoryFitRules.version}`,
      '_Face Fit Action': sameResult && ['Only Apply Size', 'Apply Size & Add to Cart', 'Add to Cart from product page after measurement'].includes(action) ? action : 'Use saved measurement',
    };
    // Account records do not store device data. Preserve it only when this page
    // just measured the very same result; never label an old scan with today's device.
    const device = sameResult && window.SpaceArmoryFitDevice?.text(this.freshMeasurement?.device);
    if (device) properties['_Face Fit Device'] = device;
    Object.assign(properties, window.SpaceArmoryFitRules.scanStatsProperties?.(
      this.savedMeasurement.scanStats || (sameResult ? this.freshMeasurement?.scanStats : null)));
    Object.entries(properties).forEach(([name,value]) => {
      const input=document.createElement('input'); input.type='hidden'; input.name=`properties[${name}]`; input.value=value; this.form.append(input);
    });
  }
  groups() { return Array.from(this.info.querySelector('variant-selects')?.querySelectorAll('.product-form__input') || []); }
  // Set pages v2 (product.bundle-kit/-duo/-trio, owner 2026-10-26): a measurement — camera, hand, account or the one
  // remembered from a mask page — sizes every mask of the set at once; a size tapped by hand stays that mask's own, and
  // "Same size? Apply L" (product-variant-picker.liquid) copies the first mask's size to a mask still without one.
  get setAll() { return this.v2 && this.bundleSizes && this.sizeIndices.length > 1; }
  // The size controls of option group `index` for `size` ("L"), or null.
  sizeControlIn(index, size) {
    return Array.from(this.groups()[index]?.querySelectorAll('input[type="radio"], select option') || [])
      .find(el => (spaceArmorySizeAliases[size] || [String(size).toLowerCase()]).includes(el.value.trim().toLowerCase())) || null;
  }
  // Every mask of the set gets `size` where it is offered; returns false when a mask does not offer it.
  fillSizes(size, values = this.values) {
    let all = true;
    this.sizeIndices.forEach(index => {
      const control = this.sizeControlIn(index, size);
      if (control) values[index] = control.value; else all = false;
    });
    return all;
  }
  clearSizes() {
    if (this.setAll) this.sizeIndices.forEach(index => { this.values[index] = ''; });
    else this.values[this.sizeIndex] = '';
  }
  // The first mask's size, offered on each later mask that has none yet.
  syncSameSize() {
    const first = this.values[this.sizeIndices[0]] || '';
    this.sizeIndices.slice(1).forEach(index => {
      const button = this.groups()[index]?.querySelector('[data-sa-same-size]');
      if (!button) return;
      const control = first && !this.values[index] ? this.sizeControlIn(index, first) : null;
      button.hidden = !control;
      if (control) {
        button.textContent = SpaceArmoryPurchaseController.text('pdp.same_size_apply', 'Same size? Apply {size}', {size: first});
        button.dataset.size = control.value;
      }
    });
  }
  applySameSize(button) {
    if (this.pending) return;
    const group = button.closest('.product-form__input');
    const index = this.groups().indexOf(group);
    const control = this.sizeControlIn(index, button.dataset.size || '');
    if (!control) return;
    this.values[index] = control.value;
    this.rememberSize(index, control.value);
    this.rememberedApplied = false; this.removeRememberedProperties();
    this.error = ''; this.revision++;
    this.writeControls(); this.sync();
    (control.tagName === 'OPTION' ? control.parentElement : control).dispatchEvent(new Event('change', {bubbles: true}));
    // Keyboard and screen readers stay on this mask's sizes, where the button was.
    if (document.activeElement === button || !document.activeElement || document.activeElement === document.body) control.focus?.({preventScroll: true});
  }
  readValues() {
    return this.groups().map(group => (group.querySelector('input:checked') || group.querySelector('select'))?.value || '');
  }
  get size() { return this.values[this.sizeIndex] || ''; }
  get hasRequiredSizes() { return this.sizeIndices.every(index => !!this.values[index]); }
  get formElement() { return this.info.querySelector('product-form'); }
  get form() { return this.formElement?.querySelector('form'); }
  resolve(values = this.values) {
    return this.variants.find(v => v.options.length === values.length && v.options.every((value, i) => value === values[i])) || null;
  }
  writeControls() {
    this.groups().forEach((group, index) => {
      const value = this.values[index] || '';
      // Browsers save and restore radio states on reload/Back; a size is restored by admitSize only.
      if (this.sizeIndices.includes(index)) group.querySelectorAll('input[type="radio"], select').forEach(control => control.setAttribute('autocomplete', 'off'));
      group.querySelectorAll('input[type="radio"]').forEach(input => { input.checked = input.value === value; });
      const select = group.querySelector('select');
      if (select) {
        if (this.sizeIndices.includes(index) && !select.querySelector('[data-size-placeholder]')) {
          const placeholder = document.createElement('option');
          placeholder.value = ''; placeholder.textContent = window.SA_I18N?.['pdp.choose_a_size'] ?? 'Choose a size'; placeholder.dataset.sizePlaceholder = ''; placeholder.disabled = true;
          select.prepend(placeholder);
        }
        select.value = value;
      }
      const selectedText = group.querySelector('legend [data-selected-value]');
      if (selectedText) selectedText.textContent = window.SA_I18N?.[`option_value.${value}`] ?? value;
    });
    this.mirrorChecked();
  }
  // Shopify renders the first size with a checked attribute. Keep that attribute equal to what is actually
  // selected, so session recordings (which replay attributes, not properties) show the shopper's real choice.
  mirrorChecked() {
    this.groups().forEach(group => group.querySelectorAll('input[type="radio"]').forEach(input => input.toggleAttribute('checked', input.checked)));
  }
  // Size is the shopper's own choice. Shopify's markup (it checks the first size, or the ?variant= size), an ad
  // or search link, and browser form restoration on reload/Back/a restored tab are not. The size starts as:
  // - the URL's size (urlSize) when the link came from inside the store (e.g. a cart line);
  // - on a fresh landing, none (a remembered or saved measurement may then preselect one);
  // - on a reload, Back/Forward or a restored tab, the size the shopper last chose for this product in this tab
  //   (per mask on bundles), or none. The URL does not decide here: it may still carry the ad's size (the shopper
  //   left before the re-render updated it), a size they changed away from, or no variant at all (a bundle with
  //   some sizes still to choose).
  admitSize(index, urlSize) {
    if (urlSize && SpaceArmoryPurchaseController.cameFromStore()) return urlSize;
    const navigation = SpaceArmoryPurchaseController.navigationType();
    if (navigation === 'navigate' || navigation === 'prerender') return '';
    return this.chosenSize(index);
  }
  static navigationType() {
    try {
      const entry = performance.getEntriesByType('navigation')[0];
      if (entry) return entry.type;
    } catch (error) {}
    // Safari before 15 has only the legacy API (1 = reload, 2 = back/forward).
    try { return ({1: 'reload', 2: 'back_forward'})[performance.navigation.type] || 'navigate'; } catch (error) { return 'navigate'; }
  }
  static cameFromStore() {
    try { return !!document.referrer && new URL(document.referrer).origin === location.origin; } catch (error) { return false; }
  }
  // The size the shopper last picked per product in this tab, per size option ({"0": "L"}; sessionStorage is per
  // tab). Only the last pick counts: a size tapped and then changed away from is no longer the shopper's choice.
  // Without storage nothing is remembered, so a reload or Back shows no size rather than one they may not have chosen.
  get chosenSizesKey() { return `sa-chosen-sizes:${this.root.dataset.productId || location.pathname}`; }
  chosenSizes() {
    try {
      const chosen = JSON.parse(sessionStorage.getItem(this.chosenSizesKey));
      return chosen && typeof chosen === 'object' && !Array.isArray(chosen) ? chosen : {};
    } catch (error) { return {}; }
  }
  // '' unless this page still offers that size.
  chosenSize(index) {
    const value = this.chosenSizes()[index];
    if (typeof value !== 'string' || !value) return '';
    const controls = this.groups()[index]?.querySelectorAll('input[type="radio"], select option') || [];
    return Array.from(controls).some(control => control.value === value) ? value : '';
  }
  choseSize(index, value) { return !!value && this.chosenSize(index) === value; }
  rememberSize(index, value) {
    if (!value) return;
    const chosen = this.chosenSizes();
    chosen[index] = value;
    try { sessionStorage.setItem(this.chosenSizesKey, JSON.stringify(chosen)); } catch (error) {}
  }
  onChange(event) {
    const group = event.target.closest('variant-selects .product-form__input');
    if (!group || !event.target.matches('input[type="radio"], select')) return;
    // A tap or keyboard choice (trusted), not a saved-measurement preselect or a script.
    const groupIndex = this.groups().indexOf(group);
    if (event.isTrusted && !this.applyingProfile && !this.pending && this.sizeIndices.includes(groupIndex)) this.rememberSize(groupIndex, event.target.value);
    if (this.pending) {
      event.stopImmediatePropagation(); this.writeControls(); return;
    }
    if (this.bundleSizes && this.sizeIndices.includes(this.groups().indexOf(group))) {
      this.sizeIndex = this.groups().indexOf(group);
      const target = this.root.querySelector('[data-bundle-size-target]');
      if (target) target.value = String(this.sizeIndex);
    }
    if (group === this.groups()[this.sizeIndex] && !this.applyingProfile) {
      this.form?.querySelector('input[name="properties[_Face Fit Action]"]')?.remove();
    }
    if (group === this.groups()[this.sizeIndex] && this.savedMeasurement && !this.applyingProfile) {
      this.savedMeasurement = null; this.clearProfileProperties();
    }
    // A size picked by hand wins; the remembered measurement stays for its card, badge and other pages.
    if ((group === this.groups()[this.sizeIndex] || (this.setAll && this.sizeIndices.includes(groupIndex))) && this.rememberedApplied && !this.applyingProfile) {
      this.rememberedApplied = false; this.removeRememberedProperties();
    }
    this.values = this.readValues();
    this.revision++;
    this.error = '';
    this.sync();
  }
  reconcile(variant) {
    // Native section HTML may default an unchosen option. Keep the accepted choices.
    const local = this.resolve();
    if (local && variant && String(local.id) === String(variant.id)) local.available = variant.available;
    this.writeControls();
    this.sync();
    if (!this.hasRequiredSizes) { const url = new URL(location.href); url.searchParams.delete('variant'); history.replaceState(history.state, '', url); }
  }
  sync() {
    this.mirrorChecked();
    this.variant = this.resolve();
    const valid = this.hasRequiredSizes && !!this.variant;
    const available = valid && this.variant.available;
    const busy = !!this.pending;
    this.info.querySelectorAll('form input[name="id"]').forEach(input => {
      input.value = valid ? String(this.variant.id) : '';
      input.disabled = !valid;
    });
    const stickyPrice = this.stickyPrices?.variants?.[this.variant?.id] || this.stickyPrices?.default;
    const priceText = this.info.querySelector('[data-sticky-price]');
    if (priceText && stickyPrice) priceText.textContent = stickyPrice.display;
    const label = busy ? SpaceArmoryPurchaseController.text('pdp.adding', 'Adding…') : !this.hasRequiredSizes ? SpaceArmoryPurchaseController.text('pdp.add_to_cart', 'Add to Cart') :
      !valid ? SpaceArmoryPurchaseController.text('pdp.unavailable_size', 'Unavailable — {size}', {size: this.size}) : !available ? SpaceArmoryPurchaseController.text('pdp.sold_out_size', 'Sold Out — {size}', {size: this.size}) : (this.bundleSizes && this.sizeIndices.length > 1 ? SpaceArmoryPurchaseController.text('pdp.add_to_cart', 'Add to Cart') : SpaceArmoryPurchaseController.text('pdp.add_to_cart_size', 'Add to Cart — {size}', {size: this.size}));
    // Set pages v2: "Add to Cart · L — $134.99" when every mask has the same size, else "Add to Cart — $134.99".
    const oneSize = !this.setAll || this.sizeIndices.every(index => this.values[index] === this.size);
    const v2Ready = this.v2 && !busy && valid && available && (!(this.bundleSizes && this.sizeIndices.length > 1) || this.setAll);
    this.info.querySelectorAll('[data-purchase-primary]').forEach(button => {
      const sticky = !!button.closest('.sticky_add_btn_wrapper');
      let buttonLabel = !this.hasRequiredSizes && !busy && sticky ? (button.dataset.selectSizeLabel || SpaceArmoryPurchaseController.text('pdp.select_a_size', 'SELECT A SIZE')) : label;
      if (v2Ready && oneSize) buttonLabel = sticky ? SpaceArmoryPurchaseController.text('pdp.add_to_cart_dot', 'Add to Cart · {size}', {size: this.size}) : SpaceArmoryPurchaseController.text('pdp.add_to_cart_dot_price', 'Add to Cart · {size} — {price}', {size: this.size, price: stickyPrice?.amount || ''}).replace(/ — $/, '');
      else if (v2Ready) buttonLabel = sticky ? SpaceArmoryPurchaseController.text('pdp.add_to_cart', 'Add to Cart') : `${SpaceArmoryPurchaseController.text('pdp.add_to_cart', 'Add to Cart')} — ${stickyPrice?.amount || ''}`.replace(/ — $/, '');
      const text = button.querySelector('[data-entry-title]');
      if (text) {
        text.textContent = buttonLabel;
        // While adding, the logo's helmet pulses its eyes before "Adding…" (brand language, owner 2026-10-15).
        const helmet = busy && document.querySelector('template[data-sa-icon="helmet"]')?.content.querySelector('svg')?.cloneNode(true);
        if (helmet) { helmet.classList.add('sa-helmet--think', 'sa-entry-helmet'); text.prepend(helmet); }
        if (this.savedMeasurement && !sticky && !this.v2) {
          const caption=document.createElement('small');caption.className='face-fit__saved-caption';
          const shown=this.measurementShown(this.savedMeasurement);
          caption.textContent=SpaceArmoryPurchaseController.text('pdp.saved_measurement', 'Saved measurement: {value} {unit}', {value: SpaceArmoryPurchaseController.decimal(shown.value), unit: shown.unit});
          text.append(caption);
        }
      }
      else button.textContent = buttonLabel;
      button.disabled = busy || (this.hasRequiredSizes && !available);
      button.setAttribute('aria-busy', String(busy));
      button.dataset.mode = this.hasRequiredSizes ? 'cart' : 'select';
    });
    const group = this.groups()[this.sizeIndex];
    this.sizeIndices.forEach(index => this.groups()[index]?.classList.add('face-fit-size-options'));
    const row = this.root.querySelector('[data-purchase-row]');
    if (row) row.dataset.hasSize = String(this.hasRequiredSizes);
    const scanOpen = this.root.querySelector('[data-purchase-open]');
    if (scanOpen) { scanOpen.hidden = this.hasRequiredSizes; scanOpen.disabled = busy; }
    const cart = this.root.querySelector('[data-purchase-cart]');
    // On bundles Add to Cart stays visible like on mask pages; without every size it asks for them instead of adding.
    if (cart) cart.hidden = !this.v2 && !this.bundleSizes && !this.hasRequiredSizes;
    const compactManual = this.root.querySelector('[data-purchase-manual]');
    if (compactManual) { compactManual.hidden = !this.v2 && !this.hasRequiredSizes; compactManual.disabled = busy; }
    const manualGuide = this.info.querySelector('manual-size-guide');
    if (manualGuide) {
      manualGuide.classList.toggle('face-fit__manual-collapsed', this.hasRequiredSizes);
      const manualOpen = manualGuide.querySelector('[data-manual-guide-open]');
      if (manualOpen) manualOpen.hidden = this.hasRequiredSizes;
    }
    const rescan = this.root.querySelector('[data-purchase-rescan]');
    if (rescan) {
      rescan.hidden = !this.hasRequiredSizes;
      rescan.disabled = busy;
      const text = rescan.querySelector('.face-fit__entry-title') || rescan;
      text.textContent = this.hasCompletedScan ? SpaceArmoryPurchaseController.text('pdp.measure_face_again', 'Measure My Face Again') : SpaceArmoryPurchaseController.text('pdp.measure_my_face', 'Measure My Face');
    }
    const status = this.root.querySelector('[data-purchase-status]');
    const message = this.error || (this.hasRequiredSizes && !available ? (!valid ? SpaceArmoryPurchaseController.text('pdp.combination_unavailable', 'This combination is unavailable. Choose another size or option.') : SpaceArmoryPurchaseController.text('pdp.size_sold_out', 'Size {size} is sold out. Choose another size or option.', {size: this.size})) : '');
    if (status) { status.textContent = message; status.hidden = !message; }
    const stickyStatus = this.info.querySelector('[data-sticky-status]');
    if (stickyStatus) { stickyStatus.textContent = message; stickyStatus.hidden = !message; }
    // The native submit remains in its original form; visible controls are proxies.
    const native = this.formElement?.querySelector('[type="submit"]');
    if (native) { native.disabled = !available || busy; native.setAttribute('aria-disabled', String(!available || busy)); }
    this.info.querySelectorAll('variant-selects .product-form__input').forEach(group => { group.inert = busy; group.setAttribute('aria-disabled', String(busy)); });
    this.syncManualLinks();
    if (this.setAll) this.syncSameSize();
    if (this.v2) this.syncV2();
    if (this.bundleSizes && this.hasRequiredSizes) this.info.classList.remove('sa-bundle-size-required');
    this.updateSticky();
  }
  // The visitor's sizing unit, decided in Liquid (snippets/sa-size-unit.liquid) and passed on data-size-unit: 'in' on
  // the United States country, 'cm' elsewhere and on a page rendered without the attribute.
  get sizeUnit() { return this.root.dataset.sizeUnit === 'in' ? 'in' : 'cm'; }
  // A measurement as the page prints it, {value, unit}: a hand measurement the way the calculator showed it (in the
  // unit it was typed in), any other in the visitor's unit from the printed cm (face-fit-rules.js display), so
  // "5.67 in" or "14.4 cm" for 14.4 cm, never both and never a cm the shopper did not see.
  measurementShown(measurement) {
    if (measurement.method === 'manual' && measurement.unit && measurement.value) return {value: measurement.value, unit: measurement.unit};
    const shown = window.SpaceArmoryFitRules.display(measurement.cm);
    return this.sizeUnit === 'in' ? {value: shown.inches, unit: 'in'} : {value: shown.cm, unit: 'cm'};
  }
  // The size a measurement gives: the size a hand measurement was given in the calculator, else the size rules' size.
  fitSizeOf(measurement) {
    if (!measurement) return '';
    if (measurement.method === 'manual' && ['S', 'M', 'L', 'XL', 'XXL'].includes(measurement.size)) return measurement.size;
    return window.SpaceArmoryFitRules?.sizeFor(measurement.cm) || '';
  }
  // The visitor's measurement as this page shows it: the account's, one completed or applied here (the newest of a
  // camera result and a hand measurement), or the camera one remembered from another mask page. null on a first visit
  // (and for a value outside the size chart).
  get measurement() {
    const rules = window.SpaceArmoryFitRules;
    const fresh = this.hasCompletedScan && rules?.valid(this.freshMeasurement) ? this.freshMeasurement : null;
    const measurement = this.savedMeasurement || this.manualMeasurement || fresh || this.rememberedMeasurement;
    return measurement && rules?.sizeFor(measurement.cm) ? measurement : null;
  }
  // The scanner's "Choose size manually" links lead back to the Measure rows. With a measurement those rows give way to
  // the size card, so only a first-time visitor sees the links; the close button stays. Runs on every sync and on every
  // screen the scanner shows (fitment-scanner setScreen).
  syncManualLinks() {
    // Multi-size bundles keep the Measure buttons for the next mask (it may be for someone else), so they keep the links.
    // After Measure Again the rows are back, so the links lead somewhere again.
    const measurement = this.measurement;
    const measured = (!(this.bundleSizes && this.sizeIndices.length > 1) || this.setAll) && !!measurement && this.againFor !== measurement;
    this.root.querySelectorAll('[data-manual]').forEach(link => { link.hidden = measured; });
  }
  // Mirrors the accepted size and measurement into the PDP v2 size row, finder card and sticky bar.
  syncV2() {
    const measurement = this.measurement;
    const fresh = measurement && measurement === this.freshMeasurement ? measurement : null;
    const fitSize = this.fitSizeOf(measurement);
    const aliases = {S:['s','small'],M:['m','medium'],L:['l','large'],XL:['xl','x-large','extra large'],XXL:['xxl','2xl','xx-large','extra extra large']};
    const group = this.groups()[this.sizeIndex];
    // Set pages v2: the measured size is marked on every mask's sizes.
    (this.setAll ? this.sizeIndices.map(index => this.groups()[index]) : [group]).forEach(sizes => {
      const labels = Array.from(sizes?.querySelectorAll('label[for]') || []);
      sizes?.querySelectorAll('input[type="radio"]').forEach(input => {
        const label = input.id ? labels.find(candidate => candidate.htmlFor === input.id) : null;
        if (label) label.toggleAttribute('data-sa-fit', !!fitSize && (aliases[fitSize] || []).includes(input.value.trim().toLowerCase()));
      });
    });
    const range = group?.querySelector('input:checked + label [data-sa-v2-range]')?.textContent || '';
    const value = this.info.querySelector('[data-sa-v2-size-value]');
    if (value) value.textContent = this.size ? [this.size, range].filter(Boolean).join(' · ') : SpaceArmoryPurchaseController.text('pdp.select_your_size', 'Select Your Size');
    const saved = this.root.querySelector('[data-sa-v2-finder-saved]');
    const fallback = this.root.querySelector('[data-sa-v2-finder-default]');
    if (saved && fallback) {
      saved.hidden = !measurement || !fitSize || this.againFor === measurement;
      fallback.hidden = !saved.hidden;
      if (measurement && fitSize) {
        // Narrow phones wrap after "Your Size:", never inside "L · 5.5 in" / "L · 5 5/8 in" / "L · 14.2 cm".
        const shown = this.measurementShown(measurement);
        saved.querySelector('[data-sa-v2-saved-title]').textContent = `${SpaceArmoryPurchaseController.text('pdp.your_size', 'Your Size: {size}', {size: fitSize})}\u00a0·\u00a0${SpaceArmoryPurchaseController.decimal(shown.value).replace(/ /g, '\u00a0')}\u00a0${shown.unit}`;
        // "Applies to Every Mask" only means something on the next masks, not on the mask where it was measured.
        const byHand = measurement === this.manualMeasurement;
        const here = byHand || measurement === fresh || measurement.product === (this.root.dataset.productHandle || this.root.dataset.productId);
        const source = byHand ? SpaceArmoryPurchaseController.text('pdp.from_measurement', 'From Your Measurement') : SpaceArmoryPurchaseController.text('pdp.from_face_measurement', 'From Your Face Measurement');
        const caption = this.savedMeasurement ? SpaceArmoryPurchaseController.text('pdp.from_face_saved', 'From Your Face Measurement · Saved to Your Account')
          : this.setAll ? SpaceArmoryPurchaseController.text('pdp.applied_every_set_mask', '{source} · Every Mask in the Set', {source})
          : here ? source : SpaceArmoryPurchaseController.text('pdp.applies_every_mask', '{source} · Applies to Every Mask', {source});
        // Narrow phones wrap the caption after its dot, never before it (owner, 11 Oct 2026): the dot stays with the word
        // before it and the part after it moves down as one piece ("Applies to Every Mask"). On one line it reads as before.
        const [lead, tail] = caption.split(' · ');
        const captionLine = saved.querySelector('[data-sa-v2-saved-caption]');
        if (tail === undefined) captionLine.textContent = caption;
        else {
          const cut = lead.lastIndexOf(' ') + 1;
          const piece = text => Object.assign(document.createElement('span'), {className: 'sa-v2-finder__caption-part', textContent: text});
          captionLine.replaceChildren(lead.slice(0, cut), piece(`${lead.slice(cut)} ·`), ' ', piece(tail));
        }
      }
    }
    const stickySize = this.info.querySelector('[data-sa-v2-sticky-size]');
    if (stickySize) { stickySize.textContent = this.size ? SpaceArmoryPurchaseController.text('pdp.sticky_size', 'Size {size} · ', {size: this.size}) : ''; stickySize.hidden = !this.size; }
    const compare = this.info.querySelector('[data-sa-v2-sticky-compare]');
    const compareText = this.stickyPrices?.variants?.[this.variant?.id]?.compare;
    if (compare && compareText) compare.textContent = compareText;
    if (this.hasRequiredSizes) this.info.classList.remove('sa-v2-size-required');
  }
  openScanner() { this.root.dispatchEvent(new CustomEvent('space-armory:open-scanner')); }
  // Measure Again (owner, 10 Oct): the size card gives way to the two Measure rows, Measure My Face and Measure by Hand.
  // The size and its YOUR FIT mark stay; a new measurement brings the card back with its own numbers.
  measureAgain() {
    const measurement = this.measurement;
    if (!measurement || this.againFor === measurement || this.againMotion) return;
    const finder = this.root.querySelector('.sa-v2-finder');
    const saved = finder?.querySelector('[data-sa-v2-finder-saved]');
    const rows = finder?.querySelector('[data-sa-v2-finder-default]');
    const swap = () => {
      this.againFor = measurement;
      this.sync();
      rows?.querySelector('button')?.focus({preventScroll: true});
    };
    if (!saved || !rows || typeof finder.animate !== 'function' || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { swap(); return; }
    // The card fades up and out, the box eases to the rows' height and the rows fade in. A tap-driven change, so no
    // layout-shift cost; nothing else on the page moves faster than the box itself.
    this.againMotion = true;
    const from = finder.offsetHeight;
    const leave = saved.animate([{opacity: 1, transform: 'none'}, {opacity: 0, transform: 'translateY(-6px)'}], {duration: 160, easing: 'ease-in', fill: 'forwards'});
    leave.finished.catch(() => {}).then(() => {
      swap();
      leave.cancel();
      const to = finder.offsetHeight;
      finder.style.overflow = 'hidden';
      const grow = finder.animate([{height: `${from}px`}, {height: `${to}px`}], {duration: 260, easing: 'cubic-bezier(.2, .7, .2, 1)'});
      rows.animate([{opacity: 0, transform: 'translateY(8px)'}, {opacity: 1, transform: 'none'}], {duration: 260, delay: 60, easing: 'ease-out', fill: 'backwards'});
      grow.finished.catch(() => {}).then(() => { finder.style.overflow = ''; this.againMotion = false; });
    });
  }
  openManual(trigger) { this.info.querySelector('manual-size-guide')?.open?.(trigger); }
  primaryAction(event) {
    if (this.pending) return;
    if (!this.hasRequiredSizes) {
      // The sticky "Select Size" only scrolls to the sizes; the message is for the main Add to Cart.
      if (this.v2 && !event?.target?.closest?.('.sticky_add_btn_wrapper')) {
        this.error = SpaceArmoryPurchaseController.text('pdp.choose_size', 'Please choose your size to continue.');
        if (this.setAll) this.error = SpaceArmoryPurchaseController.text('pdp.choose_size_each', 'Please choose a size for each mask to continue.');
        this.info.classList.remove('sa-v2-size-required');
        void this.info.offsetWidth;
        this.info.classList.add('sa-v2-size-required');
        // Set pages v2: also "Choose a size for each mask" in red under the first mask still without one (sa-bundle-pdp.css).
        if (this.setAll) this.info.classList.add('sa-bundle-size-required');
        this.sync();
      } else if (!this.v2 && this.bundleSizes && !event?.target?.closest?.('.sticky_add_btn_wrapper')) {
        this.error = SpaceArmoryPurchaseController.text('pdp.choose_size_each', 'Please choose a size for each mask to continue.');
        // Also shown in red under the first mask still without a size, until every mask has one (sa-bundle-pdp.css).
        this.info.classList.add('sa-bundle-size-required');
        this.sync();
      }
      this.focusSizes(true, event);
      return;
    }
    this.addToCart(event).catch(() => {}); // Errors are rendered by the central operation.
  }
  focusSizes(animate = false, event) {
    if (this.bundleSizes) {
      this.sizeIndex = this.sizeIndices.find(index => !this.values[index]) ?? this.sizeIndex;
      const target = this.root.querySelector('[data-bundle-size-target]');
      if (target) target.value = String(this.sizeIndex);
    }
    const group = this.groups()[this.sizeIndex];
    group?.scrollIntoView({behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center'});
    if (animate && group) {
      const pointer = event?.detail > 0 || Boolean(event?.pointerType) || document.documentElement.dataset.saInput === 'pointer';
      if (pointer) {
        const active = document.activeElement;
        if (group.contains(active) || (this.info.contains(active) && active.matches('[data-purchase-primary]'))) active.blur();
      } else {
        // Keyboard: the size already chosen, else the row itself; never a size the shopper did not pick.
        const select = group.querySelector('select');
        const chosen = group.querySelector('input[type="radio"]:checked') || (select?.value ? select : null);
        if (chosen) chosen.focus({preventScroll: true});
        else {
          group.setAttribute('tabindex', '-1');
          group.focus({preventScroll: true});
        }
      }
    } else {
      group?.querySelector('input:checked, select, input[type="radio"]')?.focus({preventScroll: true});
    }
    if (animate && group) {
      clearTimeout(this.sizeAttentionTimer);
      group.classList.remove('face-fit-size-attention');
      void group.offsetWidth;
      group.classList.add('face-fit-size-attention');
      this.sizeAttentionTimer = setTimeout(() => group.classList.remove('face-fit-size-attention'), 480);
    }
  }
  applySize(size) {
    if (this.pending) throw new Error('cart_busy');
    const aliases = {S:['s','small'],M:['m','medium'],L:['l','large'],XL:['xl','x-large','extra large'],XXL:['xxl','2xl','xx-large','extra extra large']};
    const accepted = aliases[size] || [String(size).toLowerCase()];
    const group = this.groups()[this.sizeIndex];
    const control = Array.from(group?.querySelectorAll('input[type="radio"], select option') || []).find(el => accepted.includes(el.value.trim().toLowerCase()));
    if (!control) throw new Error('variant_missing');
    const values = [...this.values]; values[this.sizeIndex] = control.value;
    // Set pages v2: the measured size goes to every mask of the set.
    if (this.setAll && !this.fillSizes(size, values)) throw new Error('variant_missing');
    const variant = this.resolve(values) || (this.bundleSizes ? this.variants.find(v => v.available && v.options.every((value, index) => !values[index] || value === values[index])) : null);
    if (!variant) throw new Error('variant_missing');
    if (!variant.available) throw new Error('variant_unavailable');
    if (window.SpaceArmoryFitRules?.valid(this.freshMeasurement) && window.SpaceArmoryFitRules.sizeFor(this.freshMeasurement.cm) === size) {
      this.hasCompletedScan = true;
      // An applied camera result is newer than a hand measurement on this page (a shown one is not).
      this.manualMeasurement = null;
      this.keepManual(null);
    }
    this.values = values; this.error = ''; this.revision++;
    (this.setAll ? this.sizeIndices : [this.sizeIndex]).forEach(index => this.rememberSize(index, values[index])); // a measured or calculated size the shopper applied
    this.writeControls(); this.sync();
    (control.tagName === 'OPTION' ? control.parentElement : control).dispatchEvent(new Event('change', {bubbles: true}));
    this.sync();
    return {variant, form: this.form};
  }
  addToCart(event, beforeCartRender) {
    if (this.pending) return this.pending;
    this.variant = this.resolve();
    if (!this.hasRequiredSizes || !this.variant || !this.variant.available) {
      this.sync();
      return Promise.reject(new Error(this.variant ? 'variant_unavailable' : 'variant_missing'));
    }
    if (!this.form?.reportValidity()) return Promise.reject(new Error(SpaceArmoryPurchaseController.text('pdp.check_options', 'Please check the product options and quantity.')));
    this.error = '';
    // Defer execution one microtask so every view is locked before any event/fetch.
    const saved = this.savedMeasurement;
    this.pending = Promise.resolve().then(async () => {
      if (saved) {
        const state = await window.SpaceArmoryFitAccount.refresh();
        if (state.customer !== saved.customer || state.revision !== saved.revision || !state.measurement) {
          throw new Error(SpaceArmoryPurchaseController.text('pdp.saved_measurement_changed', 'Your saved measurement changed. Review your size and try again.'));
        }
      }
      // Before prepare-cart: its pending per-product result then sees 'Measured Size' and adds nothing twice.
      this.attachRememberedProperties();
      this.root.dispatchEvent(new CustomEvent('space-armory:prepare-cart'));
      this.attachProfileProperties();
      return this.formElement.addToCart(event, {beforeCartRender});
    }).catch(error => {
      this.error = error.message || SpaceArmoryPurchaseController.text('pdp.add_failed', 'Could not add to your cart. Please try again.');
      throw error;
    }).finally(() => {
      this.pending = null;
      // The next add decides its own attribution: a camera result shown later on this page must find a clean form.
      this.removeRememberedProperties();
      if (window.SpaceArmoryFitAccount) this.acceptProfile(window.SpaceArmoryFitAccount.state);
      this.flushRemembered();
      this.sync();
    });
    this.sync();
    return this.pending;
  }
  stickyBoundary() {
    if (this.v2) return this.root.querySelector('[data-purchase-cart]');
    return (this.hasRequiredSizes ? this.root.querySelector('[data-purchase-cart]') : this.info.querySelector('[data-manual-guide-open]')) || this.root.querySelector('[data-purchase-open]');
  }
  updateSticky() {
    const bar = this.info.querySelector('.sticky_add_btn_wrapper');
    const boundary = this.stickyBoundary();
    if (!bar || !boundary) return;
    const preview = document.getElementById('PBarNextFrameWrapper');
    if (preview && preview !== this.preview && typeof ResizeObserver === 'function') {
      this.previewObserver?.disconnect();
      this.preview = preview;
      this.previewObserver = new ResizeObserver(() => this.updateSticky());
      this.previewObserver.observe(preview);
    }
    const previewRect = preview?.getBoundingClientRect();
    const previewOffset = previewRect && previewRect.bottom > 0 && previewRect.height < window.innerHeight / 2 ? previewRect.height : 0;
    bar.style.bottom = `${previewOffset}px`;
    const rect = boundary.getBoundingClientRect();
    // Show only after the entire purchase area has passed above the viewport.
    // Being below the fold at the top of the page must never reveal the bar.
    const passedPurchaseArea = rect.height > 0 && rect.bottom <= 0;
    // Owner, 2026-10-20: the bar steps aside once the black footer (the footer group, newsletter first) comes on screen,
    // and comes back when the shopper scrolls up again.
    const footer = document.querySelector('.shopify-section-group-footer-group');
    const atFooter = !!footer && footer.getBoundingClientRect().top < window.innerHeight;
    const visible = passedPurchaseArea && !atFooter && !document.querySelector(spaceArmoryOpenOverlay);
    bar.classList.toggle('visible', visible);
    bar.inert = !visible;
    bar.setAttribute('aria-hidden', String(!visible));
  }
  destroy() {
    clearTimeout(this.sizeAttentionTimer);
    this.abort.abort(); this.intersection?.disconnect(); this.previewObserver?.disconnect();
    this.info.classList.remove('face-fit-purchase-ready');
    delete this.info.faceFitPurchase;
  }
}

(() => {
  const controllers = new Map();
  function mountPurchases() {
    const restored = new Map();
    controllers.forEach((controller, info) => {
      if (!info.isConnected || controller.root !== info.querySelector('face-fit-scanner')) {
        restored.set(`${info.dataset.section}:${controller.root.dataset.productId}`, {
          values: controller.values, sizeIndex: controller.sizeIndex, pending: controller.pending, hasCompletedScan: controller.hasCompletedScan, savedMeasurement: controller.savedMeasurement, profileKey: controller.profileKey, profileCustomer: controller.profileCustomer, hasFreshScan: controller.hasFreshScan, freshMeasurement: controller.freshMeasurement,
          rememberedMeasurement: controller.rememberedMeasurement, rememberedApplied: controller.rememberedApplied, manualMeasurement: controller.manualMeasurement, againFor: controller.againFor,
        });
        controller.destroy(); controllers.delete(info);
      }
    });
    document.querySelectorAll("product-info[id^='MainProduct']").forEach(info => {
      if (controllers.has(info) || !info.querySelector('face-fit-scanner') || !info.querySelector('product-form form')) return;
      const root = info.querySelector('face-fit-scanner');
      const controller = new SpaceArmoryPurchaseController(info, restored.get(`${info.dataset.section}:${root.dataset.productId}`));
      controllers.set(info, controller);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountPurchases, {once: true});
  else mountPurchases();
  document.addEventListener('shopify:section:load', mountPurchases);
  document.addEventListener('product-info:loaded', mountPurchases);
  new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 && (node.matches('product-info, face-fit-scanner') || node.querySelector('product-info, face-fit-scanner'))))) mountPurchases();
    // Theme quick-add modals live under body, outside the main product-info.
    // Observe the actual open state: modalClosed fires before open is removed.
    const overlayChanged = records.some(record => record.type === 'attributes'
      ? record.target.matches('dialog, modal-dialog, quick-add-modal, product-modal')
      : [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 && (node.matches(spaceArmoryOpenOverlay) || node.querySelector(spaceArmoryOpenOverlay))));
    if (overlayChanged) controllers.forEach(controller => controller.updateSticky());
  }).observe(document.documentElement, {childList: true, subtree: true, attributes: true, attributeFilter: ['open']});
})();

// Contact and FAQ pages: "Chat with us" (a link to #chat) opens the Shopify chat bubble; without the chat it stays a
// plain link. The chat element exposes show() (2026-10); older builds only had the activator button in its shadow DOM,
// newer ones nest it one level deeper (shopify-agent-runtime).
document.addEventListener('click', (event) => {
  const link = event.target.closest?.('a[href="#chat"]');
  if (!link) return;
  const chat = document.querySelector('shopify-chat');
  if (!chat) return;
  event.preventDefault();
  const open = () => {
    if (typeof chat.show === 'function') return chat.show();
    const runtime = chat.shadowRoot?.querySelector('shopify-agent-runtime');
    const activator =
      chat.shadowRoot?.querySelector('[part~="activator"]') || runtime?.shadowRoot?.querySelector('[part~="activator"]');
    activator?.click();
  };
  if (typeof chat.show !== 'function' && !customElements.get('shopify-chat')) {
    customElements.whenDefined('shopify-chat').then(open);
    return;
  }
  open();
});

// Brand language (owner 2026-10-15): the tab icon's helmet closes its eyes while the tab is hidden; back on the tab
// the icon opens them and the header logo blinks once (assets/base.css .sa-logo--blink).
(() => {
  const icons = [...document.querySelectorAll('link[rel="icon"][data-sa-favicon-off]')];
  icons.forEach((icon) => { icon.dataset.saFaviconOn = icon.getAttribute('href'); });
  document.addEventListener('visibilitychange', () => {
    const hidden = document.visibilityState === 'hidden';
    icons.forEach((icon) => icon.setAttribute('href', hidden ? icon.dataset.saFaviconOff : icon.dataset.saFaviconOn));
    if (hidden || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const logo = document.querySelector('.sa-logo');
    if (!logo) return;
    logo.classList.remove('sa-logo--wake', 'sa-logo--blink');
    void logo.offsetWidth;
    logo.classList.add('sa-logo--blink');
  });
  document.addEventListener('animationend', (event) => {
    if (event.animationName === 'sa-blink') event.target.closest('.sa-logo')?.classList.remove('sa-logo--blink');
  });
})();

// Brand language (owner 2026-10-15): a size the shopper picks settles into place with a short click (0.22 s).
document.addEventListener('change', (event) => {
  const input = event.target.closest?.('.sa-v2-sizes input[type="radio"]');
  const label = input?.checked && input.nextElementSibling;
  if (!label || label.tagName !== 'LABEL' || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  label.classList.remove('sa-size-lock');
  void label.offsetWidth;
  label.classList.add('sa-size-lock');
  label.addEventListener('animationend', () => label.classList.remove('sa-size-lock'), {once: true});
});

// Brand language (owner 2026-10-15): a visitor whose camera measurement is remembered (30 days, assets/face-fit-memory.js)
// or saved to their account sees "Your Size L" at the top of the mobile menu (not on cards: it covered the photo, owner
// 2026-10-15). Hand measurements are
// never remembered, so they never show here. Only reads the measurement; nothing about it changes.
(() => {
  const fill = () => {
    const rules = window.SpaceArmoryFitRules;
    const account = window.SpaceArmoryFitAccount?.state;
    let cm = window.SpaceArmoryFitMemory?.read?.()?.cm;
    if (typeof cm !== 'number' && account?.status === 'ready' && rules?.valid?.(account.measurement)) cm = account.measurement.cm;
    const size = typeof cm === 'number' ? rules?.sizeFor?.(cm) : '';
    const text = size ? (window.SA_I18N?.['pdp.your_size'] ?? 'Your Size: {size}').replace('{size}', size).replace(/\s*:\s*/, ' ') : '';
    document.querySelectorAll('[data-sa-your-size]').forEach((element) => {
      const label = element.querySelector('[data-sa-your-size-text]') || element;
      if (label.textContent !== text) label.textContent = text;
      element.hidden = !size;
    });
    // Measure My Face rows (owner, 2026-10-18): shown only until the visitor has a measurement.
    document.querySelectorAll('[data-sa-no-size]').forEach((element) => { element.hidden = !!size; });
  };
  let queued = false;
  const queue = () => { if (!queued) { queued = true; requestAnimationFrame(() => { queued = false; fill(); }); } };
  const start = () => {
    fill();
    window.SpaceArmoryFitAccount?.addEventListener?.('change', queue);
    window.addEventListener('pageshow', queue);
    // A measurement taken on this page shows without a reload (owner, 2026-10-20): read it again when the phone menu
    // opens, when Measure My Face saves a size, and when the visitor comes back to the tab.
    document.getElementById('Details-menu-drawer-container')?.addEventListener('toggle', (event) => { if (event.target.open) fill(); });
    document.addEventListener('space-armory:size-saved', queue);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) queue(); });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();

// Brand language (owner 2026-10-15): opening the mobile menu wakes the logo's eyes (same animation as the first visit).
document.getElementById('Details-menu-drawer-container')?.addEventListener('toggle', (event) => {
  const logo = document.querySelector('.sa-logo');
  if (!event.target.open || !logo || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  logo.classList.remove('sa-logo--wake', 'sa-logo--blink');
  void logo.offsetWidth;
  logo.classList.add('sa-logo--wake');
});
